import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { readableApiError } from '../utils/apiError';
import { Loader2, Send, Mail, Eye, MousePointerClick, XCircle, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Live preview of the exact branded email recipients will receive. */
export const CampaignPreviewDialog = ({ campaignId, open, onOpenChange, token, onSend, canSend }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!open || !campaignId) return;
    setLoading(true);
    axios.get(`${API}/marketing/campaigns/${campaignId}/preview`, auth())
      .then(res => setData(res.data))
      .catch(err => toast.error(readableApiError(err, 'Could not load preview')))
      .finally(() => setLoading(false));
  }, [open, campaignId, auth]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/marketing/campaigns/${campaignId}/send-test`, {}, auth());
      toast.success(`Test email sent to ${res.data.sent_to}`);
    } catch (err) {
      toast.error(readableApiError(err, 'Test send failed'));
    }
    setTesting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" data-testid="campaign-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="h-4 w-4 text-indigo-400" /> Email preview</DialogTitle>
          <DialogDescription>Exactly what recipients will see in their inbox.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : data && (
          <>
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-2.5 text-sm space-y-0.5">
              <p><span className="text-muted-foreground">From:</span> {data.from}</p>
              <p><span className="text-muted-foreground">Subject:</span> <span className="font-semibold" data-testid="preview-subject">{data.subject}</span></p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Users className="h-3 w-3" /> Will go to ~{data.recipients_estimate} subscriber{data.recipients_estimate === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden rounded-lg border border-border bg-white">
              <iframe
                title="Email preview"
                srcDoc={data.html}
                sandbox=""
                className="w-full h-[420px]"
                data-testid="preview-iframe"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-end pt-1">
              <Button variant="outline" size="sm" onClick={sendTest} disabled={testing} data-testid="send-test-btn">
                {testing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                Send test to my email
              </Button>
              {canSend && (
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { onOpenChange(false); onSend?.(); }} data-testid="preview-send-btn">
                  <Send className="h-3.5 w-3.5 mr-1" /> Send to {data.recipients_estimate} subscriber{data.recipients_estimate === 1 ? '' : 's'}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="rounded-lg border border-border p-3 text-center">
    <Icon className={`h-4 w-4 mx-auto mb-1 ${accent}`} />
    <p className="text-xl font-bold leading-none">{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
    {sub && <p className={`text-[11px] font-semibold ${accent}`}>{sub}</p>}
  </div>
);

/** Delivery + engagement report for a sent campaign. */
export const CampaignReportDialog = ({ campaignId, open, onOpenChange, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !campaignId) return;
    setLoading(true);
    axios.get(`${API}/marketing/campaigns/${campaignId}/report`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(err => toast.error(readableApiError(err, 'Could not load report')))
      .finally(() => setLoading(false));
  }, [open, campaignId, token]);

  const s = data?.stats;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaign-report-dialog">
        <DialogHeader>
          <DialogTitle>Campaign report{data ? ` — ${data.campaign.name}` : ''}</DialogTitle>
          <DialogDescription>
            {data?.campaign?.sent_at ? `Sent ${new Date(data.campaign.sent_at).toLocaleString()}` : 'Delivery and engagement'}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="report-stats">
              <StatCard icon={Send} label="Delivered" value={s.sent} sub={s.failed ? `${s.failed} failed` : null} accent="text-emerald-400" />
              <StatCard icon={Eye} label="Opens" value={s.unique_opens} sub={`${s.open_rate}% open rate`} accent="text-indigo-400" />
              <StatCard icon={MousePointerClick} label="Clicks" value={s.unique_clicks} sub={`${s.click_rate}% click rate`} accent="text-fuchsia-400" />
              <StatCard icon={XCircle} label="Skipped" value={s.unsubscribed_skipped || 0} sub="unsubscribed" accent="text-amber-400" />
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-xs" data-testid="report-recipients">
                <thead className="bg-muted/50">
                  <tr className="text-left text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Recipient</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium text-center">Opens</th>
                    <th className="px-3 py-2 font-medium text-center">Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recipients || []).length === 0 ? (
                    <tr><td colSpan="4" className="px-3 py-6 text-center text-muted-foreground">No recipient data recorded</td></tr>
                  ) : data.recipients.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2">
                        <span className="font-medium">{r.name || '—'}</span>
                        <span className="text-muted-foreground ml-1.5">{r.email}</span>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[9px] capitalize ${r.status === 'sent' ? 'text-emerald-400 border-emerald-500/40' : 'text-rose-400 border-rose-500/40'}`}>{r.status}</Badge>
                      </td>
                      <td className="px-3 py-2 text-center">{r.open_count || 0}</td>
                      <td className="px-3 py-2 text-center">{r.click_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground">Opens and clicks are reported by Resend and can take a few minutes to appear. Some email apps block open tracking, so real engagement may be higher.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
